<template>
	<div class="w-full">
		<!-- Header Section -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary flex items-center gap-3 tracking-tight mb-2"
			>
				<div
					class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
				>
					<svg
						class="w-6 h-6 text-primary"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-4m-2 0v4m0-4l3 3m-3-3l-3 3"
						/>
					</svg>
				</div>
				User KYC
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				View your KYC details and documents
			</p>
		</div>

		<!-- Main Content Grid -->
		<div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
			<!-- Left Section: KYC Information -->
			<div
				class="lg:col-span-2 bg-white border border-border rounded-lg overflow-hidden"
			>
				<!-- Header -->
				<div
					class="bg-gradient-to-r from-primary to-primary-light px-6 py-4"
				>
					<h3
						class="text-lg lg:text-xl font-semibold text-white flex items-center gap-2"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						KYC Information
					</h3>
				</div>

				<!-- Body -->
				<div class="p-6 space-y-6">
					<!-- Avatar -->
					<div class="flex justify-center mb-4">
						<div class="relative group">
							<img
								src="/assets/images/placeholder.jpg"
								alt="User Avatar"
								class="w-24 h-24 rounded-full border-4 border-border shadow-sm object-cover"
							/>
							<div
								class="absolute bottom-0 right-0 w-5 h-5 bg-green-500 rounded-full border-3 border-white shadow-sm"
							></div>
						</div>
					</div>

					<div
						v-if="selectKycData && selectKycData.kraPin !== 'N/A'"
						class="space-y-4"
					>
						<!-- First Name -->
						<div
							class="flex flex-col p-4 bg-surface rounded-lg border border-border hover:border-primary/30 transition-all"
						>
							<span
								class="text-xs font-medium text-text-secondary flex items-center gap-2 mb-1 uppercase tracking-wide"
							>
								<svg
									class="w-4 h-4 text-primary"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									/>
								</svg>
								First Name
							</span>
							<span
								class="text-sm lg:text-base font-semibold text-text-primary"
							>
								{{
									selectKycData.names.firstName ||
									selectKycData.names.fname
								}}
							</span>
						</div>

						<!-- Last Name -->
						<div
							class="flex flex-col p-4 bg-surface rounded-lg border border-border hover:border-primary/30 transition-all"
						>
							<span
								class="text-xs font-medium text-text-secondary flex items-center gap-2 mb-1 uppercase tracking-wide"
							>
								<svg
									class="w-4 h-4 text-primary"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									/>
								</svg>
								Last Name
							</span>
							<span
								class="text-sm lg:text-base font-semibold text-text-primary"
							>
								{{
									selectKycData.names.lastName ||
									selectKycData.names.lname
								}}
							</span>
						</div>

						<!-- Email -->
						<div
							class="flex flex-col p-4 bg-surface rounded-lg border border-border hover:border-primary/30 transition-all"
						>
							<span
								class="text-xs font-medium text-text-secondary flex items-center gap-2 mb-1 uppercase tracking-wide"
							>
								<svg
									class="w-4 h-4 text-primary"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
									/>
								</svg>
								Email
							</span>
							<span
								class="text-sm lg:text-base font-semibold text-text-primary break-all"
							>
								{{
									selectKycData.email || selectAuthData.email
								}}
							</span>
						</div>

						<!-- KRA Pin -->
						<div
							class="flex flex-col p-4 bg-surface rounded-lg border border-border hover:border-primary/30 transition-all"
						>
							<span
								class="text-xs font-medium text-text-secondary flex items-center gap-2 mb-1 uppercase tracking-wide"
							>
								<svg
									class="w-4 h-4 text-primary"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
									/>
								</svg>
								KRA Pin
							</span>
							<span
								class="text-sm lg:text-base font-semibold text-text-primary"
							>
								{{ selectKycData.kraPin }}
							</span>
						</div>

						<!-- Phone -->
						<div
							class="flex flex-col p-4 bg-surface rounded-lg border border-border hover:border-primary/30 transition-all"
						>
							<span
								class="text-xs font-medium text-text-secondary flex items-center gap-2 mb-1 uppercase tracking-wide"
							>
								<svg
									class="w-4 h-4 text-primary"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
									/>
								</svg>
								Phone
							</span>
							<span
								class="text-sm lg:text-base font-semibold text-text-primary"
							>
								{{ selectKycData.phone }}
							</span>
						</div>
					</div>

					<!-- No Data -->
					<div
						v-else
						class="flex flex-col items-center text-text-secondary py-8"
					>
						<div
							class="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3"
						>
							<svg
								class="w-8 h-8 text-primary"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<p
							class="text-base font-semibold text-text-primary mb-1"
						>
							No KYC Details Found
						</p>
						<p class="text-xs text-text-secondary">
							Please complete your KYC verification
						</p>
					</div>
				</div>
			</div>

			<!-- Right Section: KYC Documents and Images -->
			<div class="lg:col-span-3 space-y-6">
				<!-- KYC Documents -->
				<div
					class="bg-white border border-border rounded-lg overflow-hidden"
				>
					<!-- Header -->
					<div class="bg-brand-cyan px-6 py-4">
						<h3
							class="text-lg lg:text-xl font-semibold text-white flex items-center gap-2"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
								/>
							</svg>
							KYC Documents
						</h3>
					</div>

					<!-- Body -->
					<div class="p-6">
						<div
							v-if="kyc_docs && kyc_docs.length"
							class="space-y-3"
						>
							<div
								v-for="doc in kyc_docs"
								:key="doc.id"
								class="group flex items-center gap-4 p-4 bg-surface rounded-lg border border-border hover:border-brand-cyan/50 hover:shadow-sm transition-all"
							>
								<div
									class="w-11 h-11 rounded-lg bg-brand-cyan/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-cyan/20 transition-colors"
								>
									<svg
										class="w-5 h-5 text-brand-cyan"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
								</div>
								<div class="flex-1 min-w-0">
									<p
										class="text-sm lg:text-base font-semibold text-text-primary mb-1"
									>
										{{ doc.name }}
									</p>
									<p class="text-xs text-text-secondary">
										Uploaded:
										{{
											moment(doc.uploadedAt).format(
												'MMM DD, YYYY',
											)
										}}
									</p>
								</div>
								<a
									:href="doc.url"
									target="_blank"
									class="flex-shrink-0 w-9 h-9 flex items-center justify-center text-brand-cyan hover:text-white hover:bg-brand-cyan rounded-lg transition-all group-hover:scale-105"
								>
									<svg
										class="w-5 h-5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
										/>
									</svg>
								</a>
							</div>
						</div>
						<div
							v-else
							class="flex flex-col items-center text-text-secondary py-8"
						>
							<div
								class="w-16 h-16 rounded-full bg-brand-cyan/10 flex items-center justify-center mb-3"
							>
								<svg
									class="w-8 h-8 text-brand-cyan"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
							</div>
							<p
								class="text-base font-semibold text-text-primary mb-1"
							>
								No Documents Found
							</p>
							<p class="text-xs text-text-secondary">
								Upload your KYC documents to get started
							</p>
						</div>
					</div>
				</div>

				<!-- KYC Images -->
				<div
					class="bg-white border border-border rounded-lg overflow-hidden"
				>
					<!-- Header -->
					<div class="bg-accent px-6 py-4">
						<h3
							class="text-lg lg:text-xl font-semibold text-white flex items-center gap-2"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
							KYC Images
						</h3>
					</div>

					<!-- Body -->
					<div class="p-6">
						<div
							v-if="kyc_images && kyc_images.length"
							class="space-y-3"
						>
							<div
								v-for="image in kyc_images"
								:key="image.id"
								class="group flex items-center gap-4 p-4 bg-surface rounded-lg border border-border hover:border-accent/50 hover:shadow-sm transition-all"
							>
								<div class="relative flex-shrink-0">
									<img
										:src="image.url"
										alt="KYC Image"
										class="w-16 h-16 object-cover rounded-lg border border-border group-hover:border-accent/50 transition-all shadow-sm"
									/>
								</div>
								<div class="flex-1 min-w-0">
									<p
										class="text-sm lg:text-base font-semibold text-text-primary mb-1"
									>
										{{ image.name }}
									</p>
									<p class="text-xs text-text-secondary">
										Uploaded:
										{{
											moment(image.uploadedAt).format(
												'MMM DD, YYYY',
											)
										}}
									</p>
								</div>
								<a
									:href="image.url"
									target="_blank"
									class="flex-shrink-0 w-9 h-9 flex items-center justify-center text-accent hover:text-white hover:bg-accent rounded-lg transition-all group-hover:scale-105"
								>
									<svg
										class="w-5 h-5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
										/>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
										/>
									</svg>
								</a>
							</div>
						</div>
						<div
							v-else
							class="flex flex-col items-center text-text-secondary py-8"
						>
							<div
								class="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-3"
							>
								<svg
									class="w-8 h-8 text-accent"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<p
								class="text-base font-semibold text-text-primary mb-1"
							>
								No Images Found
							</p>
							<p class="text-xs text-text-secondary">
								Upload your KYC images to complete verification
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import kyc from '../rsc/stores/user-kyc.store';
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';

export default {
	name: 'Kyc',
	beforeCreate() {
		Event.$emit('loader:show', true);
	},
	beforeDestroy() {
		this.$store.unregisterModule('kyc');
	},
	created() {
		this.$store.registerModule('kyc', kyc);
	},
	data() {
		return {
			moment,
			api: null,
		};
	},
	computed: {
		...mapGetters([
			'loading',
			'kyc_fields',
			'kyc_docs',
			'kyc_images',
			'kyc_details',
		]),
		...mapGetters('kyc', ['selectKycData']),
		...mapGetters('auth', ['selectAuthData']),
	},
	methods: {
		...mapActions(['fetchKYC']),
	},
	mounted() {
		this.$store.registerModule('kyc', kyc);
		this.fetchKYC();
	},
};
</script>

<style scoped>
/* Smooth transitions */
button,
a {
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Focus states for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid theme('colors.primary.DEFAULT');
	outline-offset: 2px;
	border-radius: 0.5rem;
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
	button,
	a {
		transition: none;
	}
}
</style>
